/**
 * useSeo.ts
 * Composable for managing SEO meta tags dynamically on each route
 * Usage: const seo = useSeo(); seo.setPageMeta({ title, description, keywords, image })
 */

export interface PageMeta {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string; // 'website', 'article', etc.
  author?: string;
  canonicalUrl?: string;
  locale?: string;
  /** Set only for routes that must pin robots. Omitted leaves the current tag. */
  robots?: string;
}

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

export default function useSeo() {
  /**
   * Update page meta tags
   */
  const setPageMeta = (meta: PageMeta) => {
    // Update title
    if (meta.title) {
      document.title = meta.title;
      updateMetaTag("title", meta.title);
      updateMetaTag("og:title", meta.title);
      updateMetaTag("twitter:title", meta.title);
    }

    // Update description
    if (meta.description) {
      updateMetaTag("description", meta.description);
      updateMetaTag("og:description", meta.description);
      updateMetaTag("twitter:description", meta.description);
    }

    // Update keywords
    if (meta.keywords) {
      updateMetaTag("keywords", meta.keywords);
    }

    // Update image
    if (meta.image) {
      updateMetaTag("og:image", meta.image);
      //updateMetaTag("twitter:image", meta.image);
    }

    // Update URL
    if (meta.url) {
      updateMetaTag("og:url", meta.url);
      updateMetaTag("twitter:url", meta.url);
    }

    // Update type
    if (meta.type) {
      updateMetaTag("og:type", meta.type);
    }

    // Update author
    if (meta.author) {
      updateMetaTag("author", meta.author);
    }

    // Update canonical URL
    if (meta.canonicalUrl) {
      updateCanonicalLink(meta.canonicalUrl);
    }

    if (meta.locale) {
      updateMetaTag("og:locale", meta.locale);
    }

    if (meta.robots) {
      updateMetaTag("robots", meta.robots);
    }
  };

  /**
   * Update or create a meta tag
   */
  const updateMetaTag = (name: string, content: string) => {
    let tag = document.querySelector(
      `meta[name="${name}"], meta[property="${name}"]`,
    ) as HTMLMetaElement;

    if (!tag) {
      tag = document.createElement("meta");
      if (name.startsWith("og:") || name.startsWith("twitter:")) {
        tag.setAttribute("property", name);
      } else {
        tag.setAttribute("name", name);
      }
      document.head.appendChild(tag);
    }

    tag.content = content;
  };

  /**
   * Update canonical link
   */
  const updateCanonicalLink = (url: string) => {
    let link = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement;

    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }

    link.href = url;
  };

  /**
   * Replace every hreflang alternate. Language pairs share en / he / x-default;
   * other routes keep a single English self link.
   */
  const setHreflangAlternates = (alternates: HreflangAlternate[]) => {
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((node) => node.remove());
    for (const alt of alternates) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = alt.hreflang;
      link.href = alt.href;
      document.head.appendChild(link);
    }
  };

  /**
   * Set structured data (JSON-LD)
   */
  const setStructuredData = (schema: any) => {
    // Remove existing structured data
    const existingScript = document.querySelector(
      'script[type="application/ld+json"][data-seo-managed]',
    );
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-managed", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  };

  /**
   * Set breadcrumb structured data
   */
  const setBreadcrumbs = (
    breadcrumbs: Array<{ name: string; url: string }>,
  ) => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: crumb.url,
      })),
    };

    setStructuredData(schema);
  };

  /**
   * Set article structured data
   */
  const setArticleData = (article: {
    headline: string;
    description: string;
    author: string;
    datePublished: string;
    dateModified?: string;
    image?: string;
    url: string;
  }) => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline,
      description: article.description,
      author: {
        "@type": "Person",
        name: article.author,
      },
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      image: article.image,
      url: article.url,
    };

    setStructuredData(schema);
  };

  /**
   * Reset to default page meta
   */
  const resetPageMeta = () => {
    setPageMeta({
      title: "Free Online Math Whiteboard for Teachers & Students | MathBoard",
      description:
        "Free online math whiteboard for teachers and students. Teach a live class or practice with an AI coach — a math board for teaching and solo study, with no download.",
      keywords:
        "math whiteboard, mathwhiteboard, math board, maths whiteboard, math whiteboard online, online whiteboard for math, math board for teaching, free online whiteboard for teaching math, AI practice coach",
      image: "https://themathboard.com/og-image.png",
      url: "https://themathboard.com/",
      canonicalUrl: "https://themathboard.com/",
      locale: "en_US",
    });
    setHreflangAlternates([
      { hreflang: "en", href: "https://themathboard.com/" },
    ]);
  };

  return {
    setPageMeta,
    updateMetaTag,
    updateCanonicalLink,
    setHreflangAlternates,
    setStructuredData,
    setBreadcrumbs,
    setArticleData,
    resetPageMeta,
  };
}
